import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Collapse,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DataObject as ContextIcon,
} from '@mui/icons-material';

/**
 * FlexContextPanel - Panel for entering context variables before script execution
 *
 * Features:
 * - Add/remove context key-value pairs
 * - Support different value types (string, number, boolean, JSON)
 * - Load defaults from logged-in user
 * - Collapsible panel
 */
function FlexContextPanel({
  context = {},
  onChange,
  isCollapsed = true,
  onToggleCollapse,
  warehouseId = null,
  embedded = false, // When true, renders without collapse header for right panel
}) {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newType, setNewType] = useState('string');
  const [editingKey, setEditingKey] = useState(null); // key currently being edited
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('');

  // Get logged-in user info from localStorage
  const getLoggedInUser = () => {
    try {
      const userJson = localStorage.getItem('waide_user');
      if (userJson) {
        return JSON.parse(userJson);
      }
    } catch (e) {
      console.error('Failed to parse user from localStorage:', e);
    }
    return null;
  };

  // Load default context values from logged-in user
  const loadDefaults = useCallback(() => {
    const user = getLoggedInUser();
    const defaults = {};

    if (user) {
      if (user.id) defaults.user_id = user.id;
      if (user.tenantId) defaults.tenant_id = user.tenantId;
      if (user.email) defaults.username = user.email;
    }

    // Use warehouseId prop if provided, otherwise try from user
    if (warehouseId) {
      defaults.warehouse_id = warehouseId;
    } else if (user?.warehouseId) {
      defaults.warehouse_id = user.warehouseId;
    }

    onChange({ ...defaults, ...context });
  }, [context, onChange, warehouseId]);

  // Parse value based on type
  const parseValue = (value, type) => {
    switch (type) {
      case 'number':
        return parseFloat(value) || 0;
      case 'boolean':
        return value === 'true' || value === true;
      case 'json':
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      case 'null':
        return null;
      default:
        return value;
    }
  };

  // Add new context variable
  const handleAdd = useCallback(() => {
    if (!newKey.trim()) return;

    const parsedValue = parseValue(newValue, newType);
    onChange({
      ...context,
      [newKey.trim()]: parsedValue,
    });

    setNewKey('');
    setNewValue('');
    setNewType('string');
  }, [newKey, newValue, newType, context, onChange]);

  // Remove context variable
  const handleRemove = useCallback((key) => {
    const newContext = { ...context };
    delete newContext[key];
    onChange(newContext);
  }, [context, onChange]);

  // Start editing a context entry
  const handleStartEdit = useCallback((key, value) => {
    setEditingKey(key);
    setEditKey(key);
    setEditValue(typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value ?? ''));
  }, []);

  // Save edited entry
  const handleSaveEdit = useCallback(() => {
    if (!editKey.trim() || editingKey === null) return;
    const newContext = { ...context };
    // Remove old key if renamed
    delete newContext[editingKey];
    // Parse value — try JSON first, fall back to string
    let parsedValue = editValue;
    try {
      parsedValue = JSON.parse(editValue);
    } catch {
      // Keep as string — but handle booleans and numbers
      if (editValue === 'true') parsedValue = true;
      else if (editValue === 'false') parsedValue = false;
      else if (editValue === 'null') parsedValue = null;
      else if (editValue !== '' && !isNaN(editValue)) parsedValue = parseFloat(editValue);
    }
    newContext[editKey.trim()] = parsedValue;
    onChange(newContext);
    setEditingKey(null);
  }, [editingKey, editKey, editValue, context, onChange]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingKey(null);
  }, []);

  // Handle key press (Enter to add)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && newKey.trim()) {
      handleAdd();
    }
  };

  // Handle key press in edit mode
  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter') handleSaveEdit();
    else if (e.key === 'Escape') handleCancelEdit();
  };

  // Get display value for a context entry
  const getDisplayValue = (value) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Get type chip color
  const getTypeColor = (value) => {
    if (value === null) return 'default';
    if (typeof value === 'number') return 'primary';
    if (typeof value === 'boolean') return 'secondary';
    if (typeof value === 'object') return 'info';
    return 'default';
  };

  // Get type label
  const getTypeLabel = (value) => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const contextEntries = Object.entries(context);

  // Content component (shared between embedded and collapsed modes)
  const renderContent = () => (
    <Box sx={{ p: 2, height: embedded ? '100%' : 'auto', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Add new variable row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end', flexShrink: 0 }}>
        <TextField
          size="small"
          label="Key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ width: 100 }}
        />
        <TextField
          size="small"
          label="Value"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ flex: 1 }}
          placeholder={newType === 'json' ? '{"key": "value"}' : ''}
        />
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={newType}
            label="Type"
            onChange={(e) => setNewType(e.target.value)}
          >
            <MenuItem value="string">String</MenuItem>
            <MenuItem value="number">Number</MenuItem>
            <MenuItem value="boolean">Boolean</MenuItem>
            <MenuItem value="json">JSON</MenuItem>
            <MenuItem value="null">Null</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          onClick={handleAdd}
          disabled={!newKey.trim()}
          sx={{ minWidth: 36, px: 1 }}
        >
          <AddIcon fontSize="small" />
        </Button>
      </Box>

      {/* Current context variables */}
      {contextEntries.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic', fontSize: 11 }}
        >
          No context variables. Add variables above to pass them to the script.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {contextEntries.map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 0.75,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: editingKey === key ? 'primary.main' : 'divider',
              }}
            >
              {editingKey === key ? (
                <>
                  <TextField
                    size="small"
                    value={editKey}
                    onChange={(e) => setEditKey(e.target.value)}
                    onKeyDown={handleEditKeyPress}
                    autoFocus
                    sx={{ width: 100, '& .MuiInputBase-input': { fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, p: '4px 8px' } }}
                  />
                  <TextField
                    size="small"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleEditKeyPress}
                    sx={{ flex: 1, '& .MuiInputBase-input': { fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, p: '4px 8px' } }}
                  />
                  <Tooltip title="Save (Enter)">
                    <IconButton size="small" onClick={handleSaveEdit} sx={{ p: 0.25, color: 'success.main' }}>
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel (Esc)">
                    <IconButton size="small" onClick={handleCancelEdit} sx={{ p: 0.25 }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    onClick={() => handleStartEdit(key, value)}
                    sx={{
                      fontWeight: 600,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 11,
                      minWidth: 70,
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {key}
                  </Typography>
                  <Chip
                    label={getTypeLabel(value)}
                    size="small"
                    color={getTypeColor(value)}
                    sx={{ height: 16, fontSize: 9 }}
                  />
                  <Typography
                    variant="body2"
                    onClick={() => handleStartEdit(key, value)}
                    sx={{
                      flex: 1,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 11,
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {getDisplayValue(value)}
                  </Typography>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(key)}
                      sx={{ p: 0.25 }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Load defaults and quick add */}
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip
            title={
              <Box sx={{ fontSize: 11 }}>
                {(() => {
                  const user = getLoggedInUser();
                  if (!user) return 'No user logged in';
                  return (
                    <>
                      <div>user_id: {user.id || 'N/A'}</div>
                      <div>tenant_id: {user.tenantId || 'N/A'}</div>
                      <div>username: {user.email || 'N/A'}</div>
                      <div>warehouse_id: {warehouseId || user.warehouseId || 'N/A'}</div>
                    </>
                  );
                })()}
              </Box>
            }
            arrow
          >
            <Button
              variant="outlined"
              size="small"
              onClick={loadDefaults}
              sx={{ fontSize: 10, py: 0.25 }}
            >
              Load User Defaults
            </Button>
          </Tooltip>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: 10 }}>
            Quick add:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {['tenant_id', 'warehouse_id', 'user_id', 'order_id', 'item_id'].map((varName) => (
              <Chip
                key={varName}
                label={varName}
                size="small"
                variant="outlined"
                onClick={() => {
                  if (!context[varName]) {
                    setNewKey(varName);
                  }
                }}
                disabled={!!context[varName]}
                sx={{ fontSize: 9, height: 20 }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Embedded mode - just render content with header
  if (embedded) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Simple header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            flexShrink: 0,
          }}
        >
          <ContextIcon sx={{ color: 'info.main', fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
            Input Context
          </Typography>
          {contextEntries.length > 0 && (
            <Chip
              label={`${contextEntries.length}`}
              size="small"
              sx={{ height: 18, fontSize: 10, ml: 'auto' }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {renderContent()}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.5,
          borderBottom: isCollapsed ? 0 : 1,
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggleCollapse}
      >
        <IconButton size="small" sx={{ p: 0.5 }}>
          {isCollapsed ? (
            <ExpandIcon fontSize="small" />
          ) : (
            <CollapseIcon fontSize="small" />
          )}
        </IconButton>

        <ContextIcon sx={{ color: 'info.main', fontSize: 18 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          Input Context
        </Typography>

        {contextEntries.length > 0 && (
          <Chip
            label={`${contextEntries.length} var${contextEntries.length > 1 ? 's' : ''}`}
            size="small"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </Box>

      {/* Content */}
      <Collapse in={!isCollapsed}>
        {renderContent()}
      </Collapse>
    </Box>
  );
}

export default FlexContextPanel;
